import { MouseEvent, useState } from 'react'
import { useDomain } from 'context'
import { useLoginAlert } from 'context/LoginAlertProvider'
import styled, { css } from 'styled-components'
import {
  CompanyInformation_CompanyInformationFragment,
  CompanyInformationNotCachedQuery_CompanyInformationFragment,
  useGqlShareCompanyMutation,
} from 'generated/graphql'
import ShareModal from '@app.components/modals/ShareModal'
import { useChattingContext } from '@app.feature/chatting/chattingContext/module/ChattingContext'
import { COMPANY_TYPE_LABELS } from '@app.feature/companyInfo/constants/companyType'
import { getLimitNumText } from 'components/ActivityListCardItem/module/getLimitNumText'
import Heart from 'svgIcons/Heart'
import ShareLight from 'svgIcons/ShareLight'
import AnalysisIcon from 'svgIcons/companyInfo/AnalysisIcon'
import CbtIcon from 'svgIcons/companyInfo/CbtIcon'
import ChatIcon from 'svgIcons/companyInfo/ChatIcon'
import { LINKAREER_ACADEMY_BASE_URL } from 'utils/config'
import useToggleCompanyInterest from '../../hooks/useToggleCompanyInterest'

interface IFProps {
  data: Partial<
    CompanyInformation_CompanyInformationFragment &
      CompanyInformationNotCachedQuery_CompanyInformationFragment
  >
}

const CompanyInfoDetailHeader = ({ data }: IFProps) => {
  const { linkareer } = useDomain()
  const { checkLogin } = useLoginAlert()
  const { toggleCompanyInterest } = useToggleCompanyInterest()
  const [shareCompany] = useGqlShareCompanyMutation()
  const { openChatting } = useChattingContext()
  const [openShareModal, setOpenShareModal] = useState(false)

  const handleClickLike = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    checkLogin(() => {
      if (data.company?.id) {
        toggleCompanyInterest(data.company.id, data.viewerInterestedIn)
      }
    })
  }

  const handleCloseShareModal = () => {
    setOpenShareModal(false)
  }

  const handleClickShare = () => {
    setOpenShareModal(true)
  }

  const handleClickChatBtn = () => {
    checkLogin(() => {
      openChatting({
        type: 'reportChatOpenChatRoom',
        data: { chatRoomId: `${data.chatRoomId}` },
      })
    })
  }

  const increaseCompanyShare = () => {
    if (!data.company?.id) {
      return
    }
    shareCompany({
      variables: { companyId: data.company.id },
    })
  }

  return (
    <StyledWrapper>
      <div className="company-info">
        <div className="company-logo">
          <img src={data.thumbnailImage?.url} alt={data.company?.name} />
          {data.isRecruiting && <span className="badge-hiring">채용중</span>}
        </div>
        <div>
          <div className="company-details">
            <h1>{data.company?.name}</h1>
            <div className="company-type">
              <span>{COMPANY_TYPE_LABELS[data.companyType ?? '']}</span>
              <span>{data.companyIndustry?.secondary}</span>
            </div>
          </div>
          <div className="link-buttons">
            {data.analysisReportId && (
              <a
                href={`/company-analysis/${data.analysisReportId}`}
                target="_blank"
                rel="noreferrer noopener"
              >
                <AnalysisIcon /> 기업분석자료
              </a>
            )}
            {data.chatRoomId && (
              <button type="button" onClick={handleClickChatBtn}>
                <ChatIcon /> 실시간 채팅
              </button>
            )}
            {data.cbtExamId && (
              <a
                href={`${LINKAREER_ACADEMY_BASE_URL}/exam/detail/${data.cbtExamId}`}
                target="_blank"
                rel="noreferrer noopener"
              >
                <CbtIcon /> 인적성/필기
              </a>
            )}
          </div>
        </div>
      </div>
      <div className="action-buttons">
        <button type="button" className="like-button" onClick={handleClickLike}>
          <Heart width={16} height={16} isFilled={data.viewerInterestedIn} />{' '}
          관심기업{' '}
          {getLimitNumText({
            number: data.company?.interestCount ?? 0,
            limit: 9999,
          })}
        </button>
        <button
          type="button"
          className="share-button"
          onClick={handleClickShare}
        >
          <ShareLight color="#333" />
        </button>
      </div>
      <ShareModal
        shareLink={`https://${linkareer}/company-info/${data?.id}`}
        open={openShareModal}
        onClose={handleCloseShareModal}
        onClick={increaseCompanyShare}
        ogImage="https://linkareer.com/static/images/company-info.png"
        title={`${data.company?.name} 채용정보 | 자소서·합격후기·면접후기·Q&A - 링커리어`}
      />
    </StyledWrapper>
  )
}

export default CompanyInfoDetailHeader

const StyledWrapper = styled.header`
  ${({ theme }) => css`
    width: 1100px;

    padding: 91px 0 21px;
    display: flex;
    align-items: center;
    justify-content: space-between;

    .company-info {
      display: flex;
      align-items: center;
      gap: 20px;

      .company-logo {
        width: 140px;
        height: 100px;
        padding: 15px;
        border-radius: 5px;
        border: solid 1px ${theme.line.line_3};
        overflow: hidden;
        flex-shrink: 0;
        position: relative;
        > img {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }

        .badge-hiring {
          position: absolute;
          top: 10px;
          left: 10px;
          background: #f46161;
          color: ${theme.background.bg_8};
          font-size: 11px;
          font-weight: bold;
          border-radius: 6px;
          min-width: 37px;
          height: 23px;
          display: inline-flex;
          justify-content: center;
          align-items: center;
        }
      }

      .company-details {
        margin-bottom: 12px;
        h1 {
          font-size: 20px;
          font-weight: bold;
          line-height: 1.2;
          margin-bottom: 4px;
        }

        .company-type {
          height: 19px;
          font-size: 13px;
          line-height: 1.85;
          letter-spacing: -0.52px;
          color: ${theme.color.text_2};
          & > span:not(:last-child)::after {
            content: '';
            display: inline-block;
            vertical-align: middle;
            margin: 0 5px;
            width: 1px;
            height: 10px;
            background-color: ${theme.line.line_2};
          }
        }
      }

      .link-buttons {
        display: flex;
        gap: 10px;

        > a,
        button {
          min-width: 120px;
          display: flex;
          gap: 3px;
          align-items: center;
          justify-content: center;
          padding: 8px 16px;
          border-radius: 5px;
          background-color: #f2f6ff;
          border: none;
          font-size: 14px;
          font-weight: 500;
          color: ${theme.color.text_1};
          cursor: pointer;
        }
      }
    }

    .action-buttons {
      display: flex;
      height: 40px;
      gap: 10px;
      > button {
        background-color: ${theme.background.bg_8};
        border: 1px solid ${theme.line.line_1};
        border-radius: 8px;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        cursor: pointer;
        &.like-button {
          width: 141px;
          font-size: 13px;
          font-weight: bold;
        }
        &.share-button {
          width: 40px;
        }
      }
    }
  `}
`
